<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    statistics: {
        type: Object,
        required: true
    }
});

const selectedFieldIds = ref([]);
const hasInitializedSelection = ref(false);
const selectedMetric = ref('total_raw');
const palette = [
    '#3f3f3f',
    '#000000',
    '#666666',
];

const fields = computed(() => {
    const entries = Object.entries(props.statistics?.fields || {});

    return entries.map(([id, field]) => ({
        id,
        ...field
    }));
});

const selectableFields = computed(() =>
    fields.value.filter((field) => field.stats_available && Array.isArray(field.responses))
);

const orderedSelectedFields = computed(() => {
    const selected = new Set(selectedFieldIds.value);
    return selectableFields.value.filter((field) => selected.has(field.id));
});

const responseCount = (field) => {
    if (field?.stats && field.stats.count !== null && field.stats.count !== undefined) {
        return Number(field.stats.count);
    }

    if (Array.isArray(field?.responses)) {
        return field.responses.filter((response) => {
            const rawValue = Array.isArray(response) ? response[1] : response;
            return rawValue !== null && rawValue !== undefined && rawValue !== '';
        }).length;
    }

    return 0;
};

const parseNumericValue = (rawValue) => {
    if (rawValue === null || rawValue === undefined) {
        return null;
    }

    if (typeof rawValue === 'number') {
        return Number.isNaN(rawValue) ? null : rawValue;
    }

    if (typeof rawValue !== 'string') {
        return null;
    }

    const trimmed = rawValue.trim();

    if (!trimmed) {
        return null;
    }

    const normalized = trimmed.replace(',', '.');
    const numeric = Number(normalized);

    return Number.isNaN(numeric) ? null : numeric;
};

const series = computed(() => {
    const data = orderedSelectedFields.value.map((field) => {
        const metricValue = field?.stats?.[selectedMetric.value];
        return parseNumericValue(metricValue);
    });

    if (data.every((value) => value === null)) {
        return [];
    }

    return [
        {
            name: selectedMetric.value === 'average_raw' ? 'Average' : 'Total',
            data
        }
    ];
});

const chartLabels = computed(() => {
    return orderedSelectedFields.value.map((field) => field.title);
});

const seriesColors = computed(() => [palette[0]]);

const selectAllFields = () => {
    selectedFieldIds.value = selectableFields.value.map((field) => field.id);
};

const clearSelection = () => {
    selectedFieldIds.value = [];
};

watch(
    selectableFields,
    (newFields) => {
        if (!hasInitializedSelection.value) {
            selectedFieldIds.value = newFields.map((field) => field.id);
            hasInitializedSelection.value = true;
            return;
        }

        selectedFieldIds.value = selectedFieldIds.value.filter((id) =>
            newFields.some((field) => field.id === id)
        );
    },
    { immediate: true }
);

</script>
<template>
    <div
        v-if="selectableFields.length"
    >
        <p class="text-center text-gray-500 text-xs mb-8 max-w-lg mx-auto">
            This report is useful if you have a series of fields you want to display on a timeline, for example if each field represents a day of the month or a month of the year.
        </p>
        <div
            class="space-y-6"
        >
            <card-basic class="!mb-0">
                <div class="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h2 class="text-lg font-semibold">Compare fields</h2>
                        <p class="text-xs text-gray-500">
                            Select at least two fields to compare their totals or averages.
                        </p>
                    </div>
                    <div class="flex flex-wrap items-center gap-2">
                        <div class="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-1 text-xs">
                            <button-base
                                size="sm"
                                :style-type="selectedMetric === 'total_raw' ? 'primary' : 'default'"
                                @click="selectedMetric = 'total_raw'"
                            >
                                Total
                            </button-base>
                            <button-base
                                size="sm"
                                :style-type="selectedMetric === 'average_raw' ? 'primary' : 'default'"
                                @click="selectedMetric = 'average_raw'"
                            >
                                Average
                            </button-base>
                        </div>
                        <button-secondary
                            size="sm"
                            @click="selectAllFields"
                        >
                            Select all
                        </button-secondary>
                        <button-secondary
                            size="sm"
                            @click="clearSelection"
                        >
                            Clear
                        </button-secondary>
                    </div>
                </div>
                <div class="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    <label
                        v-for="field in selectableFields"
                        :key="field.id"
                        class="flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                    >
                        <input
                            v-model="selectedFieldIds"
                            type="checkbox"
                            class="mr-2"
                            :value="field.id"
                        />
                        <div>
                            <div class="font-medium text-gray-900">{{ field.title }}</div>
                            <div
                                class="text-xs"
                                :class="responseCount(field) > 0 ? 'text-gray-600' : 'text-gray-400'"
                            >{{ responseCount(field) }}
                                {{ _mft('form:statistics.responses').toLowerCase() }}
                            </div>
                        </div>
                    </label>
                </div>
            </card-basic>

            <card-basic class="!mb-0">
                <div
                    v-if="selectedFieldIds.length < 2"
                    class="py-8 text-center text-sm text-gray-500"
                >
                    Pick at least two fields to render the comparison chart.
                </div>
                <div
                    v-else-if="series.length === 0"
                    class="py-8 text-center text-sm text-gray-500"
                >
                    No numeric responses were found for the selected fields.
                </div>
                <chart-line
                    v-else
                    :labels="chartLabels"
                    :series="series"
                    :fill-colors="seriesColors"
                    :show-legend="true"
                    :show-tooltip="true"
                />
            </card-basic>
        </div>
    </div>
</template>
